import React, {useState, useEffect} from 'react'
import {Button, Table, Header, Segment, Message, Dropdown, Checkbox, Modal, Icon, Input} from 'semantic-ui-react'
import Dropzone from 'react-dropzone'
import $ from 'jquery'
import ContainerImportAPI from './modules/api/container_import_api'

const DECISION_OPTIONS = [
  {key: 'use_new', text: 'Use new', value: 'use_new'},
  {key: 'add',     text: 'Add to existing', value: 'add'},
  {key: 'keep',    text: 'Keep existing', value: 'keep'},
]

const fileLabel = (f) => f ? decodeURI(f.split('/').pop()) : ''

// split a comma-separated pending value into edit boxes, always with one trailing empty box to add more
const toBoxes = (val) => {
  const parts = (val || '').split(',').map((s) => s.trim()).filter((s) => s !== '')
  return [...parts, '']
}

const ContainerImportPage = () => {
  const [uploading, setUploading] = useState(false)
  const [parsing, setParsing]     = useState(false)
  const [applying, setApplying]   = useState(false)
  const [error, setError]         = useState(null)
  const [notice, setNotice]       = useState(null)
  const [filename, setFilename]   = useState(null)
  const [result, setResult]       = useState(null)   // {ready, conflicts, unmatched}
  const [decisions, setDecisions] = useState({})     // conflict index -> 'use_new' | 'keep'
  const [checked, setChecked]     = useState({})     // conflict index -> bool
  const [imports, setImports]     = useState([])
  const [logModal, setLogModal]   = useState(null)   // {filename, rows}
  const [lkPo, setLkPo]           = useState('')
  const [lkStyle, setLkStyle]     = useState('')
  const [lkResults, setLkResults] = useState(null)   // array or null (not searched yet)
  const [lkEdits, setLkEdits]     = useState({})     // rowKey -> input value
  const [lkStatus, setLkStatus]   = useState({})     // rowKey -> inline status text

  useEffect(() => { loadHistory() }, [])

  const loadHistory = () => {
    ContainerImportAPI.history((err, data) => {
      if (err || !data) return
      setImports(data.imports || [])
    })
  }

  const downloadTemplate = () => {
    const csv  = 'IHL PO #,Style #,Container #\n'
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    a.download = 'container_import_template.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0]
    if (!file) return
    setError(null); setNotice(null); setResult(null)
    setUploading(true)

    const formData = new FormData()
    formData.append('file', file, file.name)

    $.ajax({
      type: 'POST',
      url: process.env.REACT_APP_API_URL + '/api/uploads?folder=container-imports',
      data: formData, cache: false, contentType: false, processData: false, timeout: 60000,
      success: (data) => { setUploading(false); parseFile(data.filename) },
      error:   ()     => { setUploading(false); setError('There was an error uploading the file.') }
    })
  }

  const parseFile = (uploadedFilename) => {
    setParsing(true)
    setFilename(uploadedFilename)
    ContainerImportAPI.parse(uploadedFilename, (err, data) => {
      setParsing(false)
      if (err || !data || data.success === false) {
        setError((data && data.error) || 'Could not read the file.')
        return
      }
      const initial = {}
      ;(data.conflicts || []).forEach((row, i) => { initial[i] = 'use_new' })
      setDecisions(initial)
      setChecked({})
      setResult({
        ready:     data.ready     || [],
        conflicts: data.conflicts || [],
        unmatched: data.unmatched || [],
      })
    })
  }

  const applyChanges = () => {
    if (!result) return
    const rows = []
    result.ready.forEach((r) => {
      rows.push({vpoId: r.vpoId, lineIndex: r.lineIndex, newContainer: r.container, decision: 'use_new'})
    })
    result.conflicts.forEach((r, i) => {
      rows.push({vpoId: r.vpoId, lineIndex: r.lineIndex, newContainer: r.container, decision: decisions[i] || 'use_new'})
    })
    setApplying(true)
    ContainerImportAPI.commit(filename, rows, result.unmatched, (err, data) => {
      setApplying(false)
      if (err || !data || data.success === false) { setError('Apply failed.'); return }
      setNotice(`Applied ${data.applied} line(s).`)
      setResult(null); setFilename(null); setDecisions({}); setChecked({})
      loadHistory()
    })
  }

  const undoImport = (imp) => {
    if (!window.confirm(`Undo import "${fileLabel(imp.filename) || imp.id}"?\n\nThis reverts the pending container # for lines still holding this import's values.`)) return
    ContainerImportAPI.undo(imp.id, (err, data) => {
      if (err || !data || data.success === false) { setError('Undo failed.'); return }
      setNotice('Import undone.')
      loadHistory()
    })
  }

  const viewLog = (imp) => {
    ContainerImportAPI.historyRows(imp.id, (err, data) => {
      if (err || !data) return
      setLogModal({filename: imp.filename, rows: data.rows || []})
    })
  }

  const rowKey = (r) => `${r.vpoId}-${r.lineIndex}`

  const doLookup = () => {
    setError(null); setNotice(null); setLkStatus({})
    ContainerImportAPI.lookup(lkPo, lkStyle, (err, data) => {
      if (err || !data || data.success === false) { setError('Lookup failed.'); return }
      const edits = {}
      ;(data.results || []).forEach((r) => { edits[rowKey(r)] = toBoxes(r.pending) })
      setLkEdits(edits)
      setLkResults(data.results || [])
    })
  }

  const updateBox = (k, i, value) => {
    const boxes = [...(lkEdits[k] || [''])]
    boxes[i] = value
    // keep a trailing empty box available to add another value
    if (i === boxes.length - 1 && value.trim() !== '') boxes.push('')
    setLkEdits({...lkEdits, [k]: boxes})
    setLkStatus((s) => ({...s, [k]: null}))
  }

  const savePending = (r, cleared = false) => {
    setError(null)
    const k = rowKey(r)
    const value = cleared ? '' : (lkEdits[k] || []).map((s) => s.trim()).filter((s) => s !== '').join(', ')
    ContainerImportAPI.setPending(r.vpoId, r.lineIndex, value, (err, data) => {
      if (err || !data || data.success === false) {
        setLkStatus((s) => ({...s, [k]: cleared ? '✗ clear failed' : '✗ save failed'}))
        return
      }
      // reflect the saved value in this row locally + show inline confirmation
      setLkResults((rs) => (rs || []).map((x) => rowKey(x) === k ? {...x, pending: data.pending} : x))
      setLkEdits((e) => ({...e, [k]: toBoxes(data.pending)}))
      setLkStatus((s) => ({...s, [k]: cleared ? '✓ cleared' : '✓ saved'}))
      loadHistory() // manual edits are logged too, so refresh Recent changes
    })
  }

  const setCheckedDecision = (value) => {
    const next = {...decisions}
    Object.keys(checked).forEach((k) => { if (checked[k]) next[k] = value })
    setDecisions(next)
  }

  const toggleAllChecked = (value) => {
    const next = {}
    if (result) result.conflicts.forEach((r, i) => { next[i] = value })
    setChecked(next)
  }

  const overrides = result ? result.conflicts.filter((r, i) => (decisions[i] || 'use_new') === 'use_new').length : 0
  const willApply = result ? result.ready.filter((r) => r.willWrite).length + overrides : 0
  const excluded  = result ? result.unmatched.length : 0

  return (
    <div style={{padding: '20px 40px'}}>
      <Header as='h2'>Container Import</Header>

      {error  && <Message negative onDismiss={() => setError(null)}>{error}</Message>}
      {notice && <Message positive onDismiss={() => setNotice(null)}>{notice}</Message>}

      {!result &&
        <React.Fragment>
          <Segment>
            <Button basic icon labelPosition='left' onClick={downloadTemplate} style={{marginBottom: '15px'}}>
              <Icon name='download' /> Download blank template
            </Button>
            <Dropzone onDrop={onDrop} multiple={false}>
              {({getRootProps, getInputProps}) => (
                <div {...getRootProps()} style={{
                  border: '2px dashed #aaa', borderRadius: '6px', padding: '40px',
                  textAlign: 'center', cursor: 'pointer', background: '#fafafa'
                }}>
                  <input {...getInputProps()} />
                  <p style={{margin: 0}}>
                    {uploading ? 'Uploading…' : parsing ? 'Reading file…' : 'Drop the IHL Excel here, or click to select'}
                  </p>
                  <p style={{margin: '6px 0 0', color: '#999', fontSize: '12px'}}>
                    Header must be exactly: IHL PO #, Style #, Container #
                  </p>
                </div>
              )}
            </Dropzone>
          </Segment>

          <Header as='h3'>Recent changes</Header>
          <Table compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>File</Table.HeaderCell>
                <Table.HeaderCell>By</Table.HeaderCell>
                <Table.HeaderCell>When</Table.HeaderCell>
                <Table.HeaderCell>Applied</Table.HeaderCell>
                <Table.HeaderCell></Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {imports.length === 0 &&
                <Table.Row><Table.Cell colSpan='5'>No imports yet.</Table.Cell></Table.Row>}
              {imports.map((imp) => (
                <Table.Row key={imp.id}>
                  <Table.Cell>{fileLabel(imp.filename)}</Table.Cell>
                  <Table.Cell>{imp.userName}</Table.Cell>
                  <Table.Cell>{imp.createdAt}</Table.Cell>
                  <Table.Cell>{imp.appliedCount}</Table.Cell>
                  <Table.Cell>
                    <Button size='mini' basic onClick={() => viewLog(imp)}>View log</Button>
                    <Button size='mini' basic color='red' onClick={() => undoImport(imp)}>Undo</Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <Header as='h3'>Manage pending container #</Header>
          <Segment>
            <Input
              placeholder='PO # or REF #'
              value={lkPo}
              onChange={(e) => setLkPo(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') doLookup() }}
              style={{marginRight: '8px'}}
            />
            <Input
              placeholder='Style # (optional)'
              value={lkStyle}
              onChange={(e) => setLkStyle(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') doLookup() }}
              style={{marginRight: '8px'}}
            />
            <Button onClick={doLookup}>Search</Button>
          </Segment>

          {lkResults != null &&
            <Table compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>PO # / REF #</Table.HeaderCell>
                  <Table.HeaderCell>Style #</Table.HeaderCell>
                  <Table.HeaderCell>Pending Container #</Table.HeaderCell>
                  <Table.HeaderCell></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {lkResults.length === 0 &&
                  <Table.Row><Table.Cell colSpan='4'>No matches.</Table.Cell></Table.Row>}
                {lkResults.map((r) => {
                  const k = rowKey(r)
                  return (
                    <Table.Row key={k}>
                      <Table.Cell>{r.po}</Table.Cell>
                      <Table.Cell>{r.style}</Table.Cell>
                      <Table.Cell>
                        {(lkEdits[k] || ['']).map((box, i) => (
                          <Input
                            key={i}
                            size='mini'
                            placeholder={i === (lkEdits[k] || ['']).length - 1 ? 'add…' : ''}
                            value={box}
                            onChange={(e) => updateBox(k, i, e.target.value)}
                            style={{marginRight: '4px', marginBottom: '3px'}}
                          />
                        ))}
                      </Table.Cell>
                      <Table.Cell collapsing>
                        <Button size='mini' primary onClick={() => savePending(r)}>Save</Button>
                        <Button size='mini' basic color='red' onClick={() => savePending(r, true)}>Clear</Button>
                        {lkStatus[k] &&
                          <span style={{marginLeft: '8px', color: lkStatus[k].startsWith('✓') ? 'green' : 'red', fontWeight: 'bold'}}>
                            {lkStatus[k]}
                          </span>}
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>
          }
        </React.Fragment>
      }

      {result &&
        <React.Fragment>
          <div style={{marginBottom: '15px'}}>
            <Button basic onClick={() => { setResult(null); setFilename(null) }}>← Start over</Button>
            <span style={{marginLeft: '10px', color: '#666'}}>{fileLabel(filename)}</span>
          </div>

          <Header as='h3' color='green'>Ready to apply ({result.ready.length})</Header>
          <Table compact color='green'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>PO #</Table.HeaderCell>
                <Table.HeaderCell>Style #</Table.HeaderCell>
                <Table.HeaderCell>Container #</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {result.ready.length === 0 &&
                <Table.Row><Table.Cell colSpan='3'>None.</Table.Cell></Table.Row>}
              {result.ready.map((r, i) => (
                <Table.Row key={i}>
                  <Table.Cell>{r.po}</Table.Cell>
                  <Table.Cell>{r.style}</Table.Cell>
                  <Table.Cell>{r.container}{!r.willWrite && <span style={{color: '#999'}}> (no change)</span>}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <Header as='h3' color='orange'>Conflicts — pick one per row ({result.conflicts.length})</Header>
          {result.conflicts.length > 0 &&
            <div style={{marginBottom: '8px'}}>
              <Checkbox
                label='Check all'
                onChange={(e, d) => toggleAllChecked(d.checked)}
              />
              <span style={{marginLeft: '15px'}}>Set checked to: </span>
              <Button size='mini' onClick={() => setCheckedDecision('use_new')}>Use new</Button>
              <Button size='mini' onClick={() => setCheckedDecision('add')}>Add to existing</Button>
              <Button size='mini' onClick={() => setCheckedDecision('keep')}>Keep existing</Button>
            </div>
          }
          <Table compact color='orange'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell></Table.HeaderCell>
                <Table.HeaderCell>PO #</Table.HeaderCell>
                <Table.HeaderCell>Style #</Table.HeaderCell>
                <Table.HeaderCell>Container (current → incoming)</Table.HeaderCell>
                <Table.HeaderCell>Decision</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {result.conflicts.length === 0 &&
                <Table.Row><Table.Cell colSpan='5'>None.</Table.Cell></Table.Row>}
              {result.conflicts.map((r, i) => (
                <Table.Row key={i}>
                  <Table.Cell collapsing>
                    <Checkbox
                      checked={!!checked[i]}
                      onChange={(e, d) => setChecked({...checked, [i]: d.checked})}
                    />
                  </Table.Cell>
                  <Table.Cell>{r.po}</Table.Cell>
                  <Table.Cell>{r.style}</Table.Cell>
                  <Table.Cell>{(r.current || '—')} → <b>{r.container}</b></Table.Cell>
                  <Table.Cell collapsing>
                    <Dropdown
                      selection
                      compact
                      options={DECISION_OPTIONS}
                      value={decisions[i] || 'use_new'}
                      onChange={(e, d) => setDecisions({...decisions, [i]: d.value})}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <Header as='h3' color='red'>Couldn't match — excluded ({result.unmatched.length})</Header>
          <Table compact color='red'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>PO #</Table.HeaderCell>
                <Table.HeaderCell>Style #</Table.HeaderCell>
                <Table.HeaderCell>Container #</Table.HeaderCell>
                <Table.HeaderCell>Reason</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {result.unmatched.length === 0 &&
                <Table.Row><Table.Cell colSpan='4'>None.</Table.Cell></Table.Row>}
              {result.unmatched.map((r, i) => (
                <Table.Row key={i}>
                  <Table.Cell>{r.po}</Table.Cell>
                  <Table.Cell>{r.style}</Table.Cell>
                  <Table.Cell>{r.container}</Table.Cell>
                  <Table.Cell>{r.reason}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <Segment clearing>
            <span style={{marginRight: '20px'}}>
              <b>{willApply}</b> will apply · <b>{overrides}</b> override(s) · <b>{excluded}</b> excluded
            </span>
            <Button primary floated='right' loading={applying} disabled={applying} onClick={applyChanges}>
              Apply changes
            </Button>
          </Segment>
        </React.Fragment>
      }

      <Modal open={!!logModal} onClose={() => setLogModal(null)}>
        <Modal.Header>Import log — {logModal ? fileLabel(logModal.filename) : ''}</Modal.Header>
        <Modal.Content scrolling>
          <Table compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>PO #</Table.HeaderCell>
                <Table.HeaderCell>Style #</Table.HeaderCell>
                <Table.HeaderCell>Before</Table.HeaderCell>
                <Table.HeaderCell>After</Table.HeaderCell>
                <Table.HeaderCell>Result</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {logModal && logModal.rows.map((r, i) => {
                const failed = r.status === 'unmatched'
                return (
                  <Table.Row key={i} negative={failed}>
                    <Table.Cell>{r.po}</Table.Cell>
                    <Table.Cell>{r.style}</Table.Cell>
                    <Table.Cell>{failed ? '—' : (r.prevContainer || '—')}</Table.Cell>
                    <Table.Cell>{r.newContainer}</Table.Cell>
                    <Table.Cell style={{color: failed ? '#db2828' : '#21ba45'}}>
                      {failed ? `Not applied — ${r.reason}` : 'Applied'}
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setLogModal(null)}>Close</Button>
        </Modal.Actions>
      </Modal>
    </div>
  )
}

export default ContainerImportPage
